import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";
import { MdClose, MdWarningAmber } from "react-icons/md";
import { BsCheck } from "react-icons/bs";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getCitizenScoreStatusDescriptionIndicatorC6 } from "@/utils/indicatorsEsf";
import { IndicatorEsfStatusEnum } from "@/utils/enums/IndicatorEsfStatusEnum";
import { DetailedCitizenScoresIndicatorC6Data } from "@/services/esus/indicator/esf/c6/getDetailedCitizensScoresIndicatorC6";
import { Column } from "@/@types/generics/Column";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC6CitizensScoresTableColumns = {
	citizenName: Column;
	healthTeam: Column;
	microarea: Column;
	lastAttendanceStatus: Column;
	twoWeightAndHeightMetricsRegistersStatus: Column;
	twoVisitsStatus: Column;
	vaccinationStatus: Column;
	score: Column;
};

type IndicatorEsfC6CitizensScoresTableProps = {
	citizensScores: DetailedCitizenScoresIndicatorC6Data[];
	isLoading: boolean;
};

export function getIconByCitizenScoreStatusIndicatorC6(citizenScoreStatus: IndicatorEsfStatusEnum) {
	switch (citizenScoreStatus) {
		case IndicatorEsfStatusEnum.Finished:
			return (
				<BsCheck
					className="text-primary fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC6(citizenScoreStatus)}
				/>
			);
		case IndicatorEsfStatusEnum.Pending:
			return (
				<MdWarningAmber
					className="text-warning fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC6(citizenScoreStatus)}
				/>
			);
		case IndicatorEsfStatusEnum.Lost:
			return (
				<MdClose
					className="text-danger fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC6(citizenScoreStatus)}
				/>
			);
	}
}

export function IndicatorEsfC6CitizensScoresTable({
	citizensScores,
	isLoading,
}: Readonly<IndicatorEsfC6CitizensScoresTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC6CitizensScoresTableColumns>({
		citizenName: { title: "Cidadão", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		lastAttendanceStatus: { title: "Última Consulta", visible: true },
		twoWeightAndHeightMetricsRegistersStatus: { title: "Peso e Altura", visible: true },
		twoVisitsStatus: { title: "Visitas", visible: true },
		vaccinationStatus: { title: "Vacina Influenza", visible: true },
		score: { title: "Pontos", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.lastAttendanceStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastAttendanceStatus.title}
							<CardOverlayTrigger
								header="Última Consulta"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com a última consulta presencial realizada por
									<br />
									médico ou enfermeiro nos últimos 12 meses
									<br />
									(Prática A - 25 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.twoWeightAndHeightMetricsRegistersStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.twoWeightAndHeightMetricsRegistersStatus.title}
							<CardOverlayTrigger
								header="Registros de peso e altura"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com pelo menos 2 registros de peso e altura
									<br />
									nos últimos 12 meses
									<br />
									(Prática B - 25 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.twoVisitsStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.twoVisitsStatus.title}
							<CardOverlayTrigger
								header="Visitas"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com pelo menos 2 visitas realizada por ACS ou TACS
									<br />
									com intervalo mínimo de 30 dias nos últimos 12 meses
									<br />
									(Prática C - 25 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.vaccinationStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.vaccinationStatus.title}
							<CardOverlayTrigger
								header="Vacina Influenza"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Cidadão com pelo menos 1 registro de aplicação do
									immunobiologico
									<br />
									influenza trivalente(33) ou influenza tetravalente(77) nos
									últimos
									<br />
									12 meses <br />
									(Prática D - 25 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.score.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.score.title}{" "}
							<CardOverlayTrigger
								header="Pontuação"
								message="Para cada prática realizada, existe uma pontuação específica onde a soma de todas as práticas totaliza 100 pontos por cidadão"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC6CitizensScoresTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC6CitizensScoresTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC6CitizensScoresTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensScores.length > 0
					? citizensScores.map((citizenScores, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.citizenName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenScores.fullName}</div>
										<div className="text-muted">
											{dayjs(citizenScores.birthdate).format("DD/MM/YYYY")}
										</div>
									</div>
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenScores.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenScores.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenScores.area ?? "FA"}
								</td>
								<td
									className={`px-2 ${
										!columns.lastAttendanceStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC6(
										citizenScores.lastAttendanceStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.twoWeightAndHeightMetricsRegistersStatus.visible &&
										"d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC6(
										citizenScores.twoWeightAndHeightMetricsRegistersStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.twoVisitsStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC6(
										citizenScores.twoHomeVisitsStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.vaccinationStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC6(
										citizenScores.vaccinationStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.score.visible && "d-none"
									}`}
								>
									{citizenScores.score}
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) => count + (column?.visible ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
